import React, { useMemo, useState } from "react";
import { ProductCard } from "@/features/product/ProductCard";
import { ClickableCard } from "../../ClickableCard";
import { filterMatches, SearchInput } from "@/components/SearchInput";
import { AddBoxItemParams, useAccountContext } from "../../AccountContext";
import { DrawerPageProps } from "../types";
import { PortalDrawer } from "..";
import { ConfirmItemPage } from "./ConfirmItemPage";
import { Unit } from "@/helpers/Unit";
import { Cadence } from "../../types";
import { DroppsProduct, productSelect, variantSelect } from "@/store";
import { droppsProductToProductCardProduct } from "../../helpers";

type ProductSelectorPageProps = DrawerPageProps & {
  onChange: (item: AddBoxItemParams) => void;
  nextChargeDate: Date;
  initialFilter?: string;
  displayMode?: "onlySubscription" | "onlySpot" | "all";
  cadence?: Cadence;
  canUpdateInterval?: boolean;
  drawerTitle?: string;
};

const ProductSelectorPage = ({
  initialFilter = "",
  displayMode = "all",
  cadence = { frequency: 4, unit: new Unit("month") },
  onChange,
  nextChargeDate,
  canUpdateInterval = false,
  drawerTitle,
  ...rest
}: ProductSelectorPageProps) => {
  const products = useAccountContext().products;

  const [query, setQuery] = useState(initialFilter);
  const [selectedProduct, setSelectedProduct] = useState<
    DroppsProduct | undefined
  >(undefined);

  const filteredProducts = useMemo(() => {
    const baseFilter =
      displayMode !== "onlySubscription"
        ? products
        : products.filter(productSelect.defaultSellingPlan);
    return filterMatches(baseFilter, (product) => product.title, query);
  }, [products, query, displayMode]);

  return (
    <div className="space-y-4">
      <SearchInput
        id="search"
        label="Select Product"
        placeholder="Search products..."
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {filteredProducts.map((product) => {
        const variant = productSelect.defaultVariant(product);
        const productCardProduct = droppsProductToProductCardProduct({
          product,
          variant,
        });
        productCardProduct.size = "";
        return (
          <ClickableCard
            key={product.title}
            bordered={false}
            className="border border-solid border-primary-200"
            onClick={() => setSelectedProduct(product)}
          >
            <ProductCard
              className="-m-4"
              productCardValue={{
                ...productCardProduct,
                price:
                  displayMode === "onlySpot"
                    ? variant.price
                    : variantSelect.lowestPrice(variant, product),
              }}
              compact={true}
              hoverable={false}
              productTitle={
                <h3 data-id="product-card-title" className="title text-sm">
                  {productSelect.name(product)}
                </h3>
              }
            />
          </ClickableCard>
        );
      })}

      <PortalDrawer
        open={selectedProduct !== undefined}
        title={drawerTitle ? drawerTitle : "Edit Item"}
        contents={(nav) =>
          selectedProduct !== undefined ? (
            <ConfirmItemPage
              onChange={onChange}
              displayMode={displayMode}
              product={selectedProduct}
              cadence={cadence}
              nextChargeDate={nextChargeDate}
              canUpdateInterval={canUpdateInterval}
              {...nav}
            />
          ) : null
        }
        {...rest}
        onBack={() => setSelectedProduct(undefined)}
        transparentBackdrop={true}
      />
    </div>
  );
};

export { ProductSelectorPage };
